import reportApi from '@/api/report'
import { generateFormHandlerMutations } from '@/helpers/forms'

const pad = value => String(value).padStart(2, '0')

const asLocal = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  + `T${pad(date.getHours())}:${pad(date.getMinutes())}`

const startOfToday = () => {
  const date = new Date()
  date.setHours(0, 0, 0, 0)

  return asLocal(date)
}

const endOfToday = () => {
  const date = new Date()
  date.setHours(23, 59, 0, 0)

  return asLocal(date)
}

const emptyTotals = () => ({ ordersCount: 0, orderAmount: 0, paidAmount: 0 })

const state = {
  rows: [],
  summary: [],
  totals: emptyTotals(),

  statuses: [],
  restaurants: [],
  operators: [],
  sources: [],

  truncated: false,
  loading: false,
  downloading: false,
  loaded: false,
  error: null,

  filters: {
    from: startOfToday(),
    to: endOfToday(),
    statusIds: [],
    query: '',
    isLate: null,
    restaurantId: null,
    expeditionType: null,
    operatorName: null,
    source: null,
  },
}

const withSeconds = value => {
  if (!value) return null

  return value.length === 16 ? `${value}:00` : value
}

const payload = () => ({
  from: withSeconds(state.filters.from),
  to: withSeconds(state.filters.to),
  statusIds: state.filters.statusIds && state.filters.statusIds.length ? state.filters.statusIds : null,
  query: state.filters.query || null,
  isLate: state.filters.isLate || null,
  restaurantId: state.filters.restaurantId || null,
  expeditionType: state.filters.expeditionType || null,
  operatorName: state.filters.operatorName || null,
  source: state.filters.source || null,
})

const fileName = response => {
  const disposition = response.headers['content-disposition'] || ''
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)

  if (encoded) return decodeURIComponent(encoded[1])

  const plain = /filename="?([^";]+)"?/i.exec(disposition)

  return plain ? plain[1] : 'Отчет по операторам.xlsx'
}

const getters = {}

const actions = {
  fetch({ commit }) {
    commit('LOADING')

    return reportApi.getOperatorReport(payload())
      .then(({ data }) => commit('SET_REPORT', data))
      .catch(error => commit('SET_ERROR', error))
  },

  download({ commit }) {
    commit('DOWNLOADING')

    return reportApi.getOperatorReportFile(payload())
      .then(response => commit('SAVE_FILE', response))
      .catch(error => commit('SET_ERROR', error))
  },
}

const mutations = {
  LOADING(state) {
    state.loading = true
    state.error = null
  },

  DOWNLOADING(state) {
    state.downloading = true
    state.error = null
  },

  SET_REPORT(state, report) {
    state.rows = report.rows || []
    state.summary = report.summary || []
    state.totals = report.totals || emptyTotals()
    state.restaurants = report.restaurants || []
    state.operators = report.operators || []
    state.sources = report.sources || []
    state.truncated = report.truncated || false

    const known = new Map(state.statuses.map(status => [status.id, status]));
    (report.statuses || []).forEach(status => known.set(status.id, status))
    state.statuses = [...known.values()].sort((left, right) => left.id - right.id)

    state.loading = false
    state.loaded = true
  },

  SET_ERROR(state, error) {
    const response = error && error.response

    state.error = (response && response.data && (response.data.message || response.data.title))
      || 'Не удалось построить отчёт'
    state.loading = false
    state.downloading = false
  },

  SAVE_FILE(state, response) {
    const url = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName(response))
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    state.downloading = false
  },

  ...generateFormHandlerMutations(state),
}

const modules = {}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules,
}
